import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { v4 as uuid } from 'uuid';
import IllustrationsService from 'services/IllustrationsService';
import formatImageName from 'utils/formatImageName';
import buildIllustrationImageUrl, {
  IllustrationOption,
  IllustrationSize,
} from 'utils/buildIllustrationImageUrl';
import { useBreadcrumbsContext } from 'contexts/breadcrumbsContext';
import capitalizeString from 'utils/capitalizeString';
import mapIllustrationProps from 'utils/mapIllustrationProps';
import createSlug from 'utils/createSlug';
import { useGalleryContext } from 'contexts/galleryContext';
import GalleryDetailPage from 'layouts/GalleryDetailPage';
import { ThumbsPosition } from 'components/ImageSlider/types';
import IconsService from 'services/IconsService';
import { setLocale } from 'utils/locale';
import { getEnv } from 'utils';

import en from '../../locales/illustrations/slug/en';
import ja from '../../locales/illustrations/slug/en';
import zh from '../../locales/illustrations/slug/en';
import es from '../../locales/illustrations/slug/es';
import fr from '../../locales/illustrations/slug/fr';
import it from '../../locales/illustrations/slug/it';

export function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps({ params: { slug }, locale }) {
  try {
    locale = 'en';
    const response = await IllustrationsService.fetchOne(formatImageName(slug, true), locale);
    const icon = await IconsService.findOneIconDescriptionById(response.data.images[0].image_id);
    const descriptionKey = `description_${locale}`;
    const descriptionIllustration = icon.data[descriptionKey] || '';

    return {
      props: {
        data: response.data.images[0],
        descriptionIllustration,
      },
      revalidate: 604800, // 1 week
    };
  } catch (error) {
    console.error(error.message);
    return {
      props: {
        data: {},
        descriptionIllustration: '',
      },
      revalidate: 604800, // 1 week
    };
  }
}

export default function Illustration({ data, descriptionIllustration }) {
  const [, dispatchBreadcrumbs] = useBreadcrumbsContext();
  const [, dispatchGallery] = useGalleryContext();
  const [related, setRelated] = useState(undefined);
  const router = useRouter();
  const {
    query: { breadcrumbs, from },
  } = router;
  const { locale } = router;
  const t = setLocale(locale, { en, es, fr, it, ja, zh });

  useEffect(() => {
    if (!data?.image_name) return;

    function updateBreadcrumbs() {
      if (!breadcrumbs) {
        dispatchGallery({
          activeTag: undefined,
        });
        dispatchBreadcrumbs([
          {
            label: 'Illustrations',
            href: '/illustrations',
          },
          {
            label: data.image_name.replace(/_/g, ' '),
            href: undefined,
            isDisabled: true,
          },
        ]);
      } else {
        const breadcrumbsItems = breadcrumbs.split(',');
        const breadcrumbsList = [];

        breadcrumbsList.push({
          label: 'Illustrations',
          href: '/illustrations',
        });

        if (from && from === 'subcategory' && breadcrumbsItems.length === 1) {
          breadcrumbsList.push({
            label: capitalizeString(breadcrumbsItems[0]),
            href: `/illustrations/subcategory/${createSlug(breadcrumbsItems[0])}`,
          });
        } else {
          breadcrumbsList.push({
            label: capitalizeString(breadcrumbsItems[0]),
            href: `/illustrations/category/${createSlug(breadcrumbsItems[0])}`,
          });

          if (breadcrumbsItems[1]) {
            breadcrumbsList.push({
              label: capitalizeString(breadcrumbsItems[1]),
              href: `/illustrations/subcategory/${createSlug(breadcrumbsItems[1])}?breadcrumbs=${
                breadcrumbsItems[0]
              }`,
            });
          }
        }

        breadcrumbsList.push({
          label: capitalizeString(data.image_name.replace(/_/g, ' ')),
          href: undefined,
          isDisabled: true,
        });

        dispatchBreadcrumbs(breadcrumbsList);
      }
    }

    async function fetchRelated() {
      try {
        const {
          data: { images },
        } = await IllustrationsService.fetchRelated(data.image_id, locale);
        const mapped = images?.map(mapIllustrationProps);

        setRelated(mapped);
      } catch (error) {
        console.error(error.message);
      }
    }

    fetchRelated();
    updateBreadcrumbs();
  }, [data]);

  function getData() {
    return {
      title: data.image_name?.replace(/_/g, ' ') ?? '',
      tags: data.tags,
      image_id: data.image_id,
    };
  }

  function getSliderImages() {
    const mapImageOptions = (option) => ({
      id: uuid(),
      src: buildIllustrationImageUrl(data.file_name, IllustrationSize.LARGE, option, false),
      alt: data.image_name?.replace(/_/g, ' ') ?? '',
    });

    return Object.values(IllustrationOption).map(mapImageOptions);
  }

  function handleTagClick(tag) {
    window.scrollTo(0, 0);

    router.push(`${getEnv('MTG_URL_APP')}/illustrations/search/${tag}`, undefined, {
      shallow: true,
    });
  }

  return (
    <GalleryDetailPage
      isTemplatePage={false}
      data={getData()}
      related={related}
      relatedTitle={t.relatedTitle}
      sliderImages={getSliderImages()}
      sliderThumbsPosition={ThumbsPosition.Left}
      sliderInitialImage={1}
      onTagClick={handleTagClick}
      descriptionIllustration={descriptionIllustration}
    />
  );
}
